import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, ButtonBase, CircularProgress, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import useNotifications from "../../../Hooks/useNotifications";
import { NotificationItem } from "../../../Repositories/NotificationRepository";
import { resolveNotificationRoute } from "../../../config/NOTIFICATION_ROUTES";
import { getActiveBrandConfig } from "../../../config/environment.config";
import { useTailwindBrand } from "../../shared";
import { categoryIcon, formatRelativeDate, useIsClient } from "../../AppBar/NotificationList";
import { filterNotifications } from "../../../Utils/notificationGroups";

interface NotificationsWidgetProps {
    /** Nombre maximal de notifications non lues affichées. */
    limit?: number;
    /** Lien « Tout voir » en pied de widget (à désactiver si l'hôte l'affiche déjà dans son en-tête). */
    showAllLink?: boolean;
}

/**
 * Widget « Nouveautés » des tableaux de bord prof et client : les dernières notifications
 * non lues (titre, date relative, icône de catégorie). Un clic marque la notification
 * comme lue et ouvre sa destination. Partage le store de useNotifications avec la cloche.
 */
const NotificationsWidget: React.FC<NotificationsWidgetProps> = ({ limit = 5, showAllLink = true }) => {
    const { brandConfig } = useTailwindBrand();
    const navigate = useNavigate();
    const isClient = useIsClient();
    const { enabled, items, loaded, loading, refresh, markAsRead } = useNotifications();
    const primary = brandConfig.colors.primary;
    const ff = brandConfig.fontFamily;
    const notificationsUrl = `/${getActiveBrandConfig()}/tableau-de-bord/notifications`;

    // montage : recharge la liste si elle est absente ou périmée
    useEffect(() => {
        if (enabled) refresh();
    }, [enabled, refresh]);

    const unread = useMemo(
        () => filterNotifications(items, { unreadOnly: true }).slice(0, limit),
        [items, limit],
    );

    const handleClick = (item: NotificationItem) => {
        markAsRead(item);
        const destination = resolveNotificationRoute(item.target, isClient);
        if (!destination) return;
        navigate(destination.path, destination.state ? { state: destination.state } : undefined);
    };

    const renderEmpty = (text: string) => (
        <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: ff }}>
            {text}
        </Typography>
    );

    const renderBody = () => {
        if (!loaded) {
            if (loading) {
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                        <CircularProgress size={14} sx={{ color: primary }} />
                        <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: ff }}>Chargement…</Typography>
                    </Box>
                );
            }
            return renderEmpty("Notifications indisponibles pour le moment.");
        }
        if (unread.length === 0) return renderEmpty("Rien de nouveau");

        return (
            <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
                {unread.map((item) => {
                    const Icon = categoryIcon(item.category);
                    const accent = item.priority === "high" ? brandConfig.colors.error : primary;
                    return (
                        <li key={item.id}>
                            <ButtonBase
                                onClick={() => handleClick(item)}
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.25,
                                    textAlign: "left",
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: "10px",
                                    borderLeft: `3px solid ${item.priority === "high" ? brandConfig.colors.error : "transparent"}`,
                                    "&:hover": { backgroundColor: `${primary}0c` },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "8px",
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: accent,
                                        backgroundColor: `${accent}14`,
                                    }}
                                >
                                    <Icon sx={{ fontSize: 16 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: ff,
                                            fontWeight: 600,
                                            color: "text.primary",
                                            lineHeight: 1.3,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary" }}>
                                        {formatRelativeDate(item.createdAt)}
                                    </Typography>
                                </Box>
                            </ButtonBase>
                        </li>
                    );
                })}
            </Stack>
        );
    };

    return (
        <Box>
            {renderBody()}
            {showAllLink && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <ButtonBase
                        onClick={() => navigate(notificationsUrl)}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: "8px",
                            color: primary,
                            fontFamily: ff,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            "&:hover": { backgroundColor: `${primary}0f` },
                        }}
                    >
                        Tout voir
                        <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </ButtonBase>
                </Box>
            )}
        </Box>
    );
};

export default NotificationsWidget;
